// packages/ui/src/ToggleSwitchField.tsx
import React, { useId, InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

type CustomToggleProps = {
  label?: string;
  labelKey?: string;
  labelPosition?: 'left' | 'right' | 'top' | 'hidden';
  descriptionText?: string;
  descriptionTextKey?: string;
  switchClassName?: string;
  labelClassName?: string;
  containerClassName?: string;
  checked?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  id?: string;
  name?: string;
};

export interface ToggleSwitchFieldProps extends Omit<InputHTMLAttributes<HTMLButtonElement>, 'type' | 'className' | 'checked' | 'onChange' | 'disabled' | 'id' | 'name' | 'onClick'>, CustomToggleProps {}

export const ToggleSwitchField: React.FC<ToggleSwitchFieldProps> = ({
  label,
  labelKey,
  labelPosition = 'right',
  descriptionText,
  descriptionTextKey,
  checked = false,
  onChange,
  disabled,
  id,
  name,
  switchClassName,
  labelClassName,
  containerClassName,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const fieldId = id || autoId;
  const fieldName = name || fieldId;

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedDescriptionText = descriptionTextKey ? t(descriptionTextKey) : descriptionText;

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
    if (onChange && !disabled) {
      const syntheticEventTarget = {
        checked: !checked,
        name: fieldName,
        id: fieldId,
        value: String(!checked),
        type: 'checkbox',
        dataset: (event.currentTarget as HTMLElement).dataset,
      };
      const syntheticEvent = {
        target: syntheticEventTarget as unknown as HTMLInputElement,
        currentTarget: syntheticEventTarget as unknown as HTMLInputElement,
        nativeEvent: event.nativeEvent,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        defaultPrevented: event.defaultPrevented,
        eventPhase: event.eventPhase,
        isTrusted: event.isTrusted,
        preventDefault: () => event.preventDefault(),
        isDefaultPrevented: () => event.isDefaultPrevented(),
        stopPropagation: () => event.stopPropagation(),
        isPropagationStopped: () => event.isPropagationStopped(),
        persist: () => {},
        timeStamp: event.timeStamp,
        type: 'change',
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const baseShadow = 'shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)]';
  const baseTranslate = '-translate-x-px -translate-y-px';

  const SwitchVisual = () => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelPosition !== 'hidden' && resolvedLabel ? `${fieldId}-label` : undefined}
      aria-describedby={resolvedDescriptionText ? `${fieldId}-description` : undefined}
      onClick={handleToggle}
      disabled={disabled}
      className={clsx(
        'relative inline-flex items-center flex-shrink-0 h-7 w-12 border-2 border-black rounded-lg cursor-pointer transition-all ease-in-out duration-150 p-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
        // Background color
        checked ? 'bg-indigo-500' : 'bg-gray-200',
        // Focus state indicated by shadow/translate
        '[.theme-liquid-glass_&]:border-white/30',
        checked ? '[.theme-liquid-glass_&]:bg-violet-500/80' : '[.theme-liquid-glass_&]:bg-white/10',
        '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',

        // --- NEW LOGIC FOR CHECKED STATE ---
        // If checked and not disabled, it should always have the "popped" look
        checked && !disabled && [baseShadow, baseTranslate],

        // Hover, Focus, and Active states (only if not disabled)
        !disabled && {
          // On hover/focus, if NOT already checked, apply base shadow/translate.
          // If already checked, it already has the base, so we don't need to re-apply,
          // but active state will override.
          'hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] hover:-translate-x-px hover:-translate-y-px': true,
          'focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] focus:-translate-x-px focus:-translate-y-px': true,
          
          // Active state (click) will have a slightly different shadow and reset translate temporarily
          // regardless of checked state, then revert to base popped if still checked.
          'active:shadow-[1px_1px_0px_0px_rgba(0,0,0,0.9)] active:translate-x-0 active:translate-y-0': true,
        },
        
        // Disabled state
        disabled && 'cursor-not-allowed opacity-60 bg-gray-300 border-gray-400', // No shadow or translate when disabled
        switchClassName
      )}
      {...buttonProps}
    >
      <input
        type="checkbox"
        id={fieldId}
        name={fieldName}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      <span
        aria-hidden="true"
        className={clsx(
          'pointer-events-none inline-block h-4 w-4 rounded bg-white border-2 border-black transform transition-transform ease-in-out duration-150',
          checked ? 'translate-x-5' : 'translate-x-0',
          disabled && 'bg-gray-100 border-gray-500',
          '[.theme-liquid-glass_&]:border-white/40 [.theme-liquid-glass_&]:bg-white'
        )}
      />
    </button>
  );

  const LabelContent = () => resolvedLabel && labelPosition !== 'hidden' ? (
    <span
      id={`${fieldId}-label`}
      className={clsx(
        'text-sm font-medium text-gray-700 select-none',
        '[.theme-liquid-glass_&]:text-white/80',
        disabled ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer',
        labelClassName
      )}
      onClick={handleToggle}
    >
      {resolvedLabel}
    </span>
  ) : null;

  const DescriptionContent = () => resolvedDescriptionText ? (
     <p id={`${fieldId}-description`} className="text-xs text-gray-500 mt-0.5 [.theme-liquid-glass_&]:text-white/50">
        {resolvedDescriptionText}
      </p>
  ) : null;

  if (labelPosition === 'top') {
    return (
      <div className={clsx('flex flex-col items-start', containerClassName)}>
        <div className="mb-1 w-full">
          <LabelContent />
          <DescriptionContent />
        </div>
        <SwitchVisual />
      </div>
    );
  }

  return (
    <div className={clsx('flex items-center', containerClassName, {
        'justify-between': labelPosition === 'left' || labelPosition === 'right',
    })}>
      {labelPosition === 'left' && <LabelContent />}
      <div className={clsx(
        {'ml-3': labelPosition === 'left' && resolvedLabel},
        {'mr-3': labelPosition === 'right' && resolvedLabel}
      )}>
        <SwitchVisual />
      </div>
      {labelPosition === 'right' && <LabelContent />}
      {labelPosition === 'hidden' && <label htmlFor={fieldId} className="sr-only">{resolvedLabel}</label>}
      {(labelPosition === 'left' || labelPosition === 'right' || labelPosition === 'hidden') && resolvedDescriptionText && (
         <p id={`${fieldId}-description`} className={clsx("text-xs text-gray-500 [.theme-liquid-glass_&]:text-white/50", (labelPosition === 'left' ? 'ml-2' : 'mr-2'))}>
         </p>
      )}
    </div>
  );
};
